import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import ApiLimitPopup from '../components/ApiLimitPopup';
import { setApiErrorHandler } from '../utils/api';

interface ApiStatus {
  usage: number;
  limit: number;
  remaining: number;
  exceeded: boolean;
}

interface ApiUsageData {
  ROUTES: ApiStatus;
  GEMINI: ApiStatus;
  FUNCTION_PHOTOS: ApiStatus;
  PLACES: ApiStatus;
  DISCOVER: ApiStatus;
}

interface ApiLimitContextType {
  apiUsage: ApiUsageData | null;
  loading: boolean;
  anyLimitExceeded: boolean;
  checkApiLimits: () => Promise<void>;
  handleApiError: (error: any, apiType?: string) => boolean;
  isLimitReached: (apiType: keyof ApiUsageData) => boolean;
}

const ApiLimitContext = createContext<ApiLimitContextType | undefined>(undefined);

export const useApiLimits = () => {
  const context = useContext(ApiLimitContext);
  if (context === undefined) {
    throw new Error('useApiLimits must be used within an ApiLimitProvider');
  }
  return context;
};

interface ApiLimitProviderProps {
  children: ReactNode;
}

export const ApiLimitProvider: React.FC<ApiLimitProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [apiUsage, setApiUsage] = useState<ApiUsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [anyLimitExceeded, setAnyLimitExceeded] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupDetails, setPopupDetails] = useState<{
    apiType?: string;
    currentUsage?: number;
    limit?: number;
  }>({});

  const checkApiLimits = async () => {
    if (!user) {
      setApiUsage(null);
      setAnyLimitExceeded(false);
      return;
    }

    setLoading(true);
    try {
      const token = await user.getIdToken();
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://travelapp-bens.onrender.com' 
        : 'http://localhost:5001';
        
      const response = await fetch(`${API_BASE_URL}/api/user/api-usage`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setApiUsage(data.apiStatus);
        setAnyLimitExceeded(data.anyLimitExceeded);
        
        // Show popup if any limit is exceeded
        if (data.anyLimitExceeded && !showPopup) {
          setShowPopup(true);
        }
      } else {
        console.error('Failed to fetch API usage:', response.status);
      }
    } catch (error) {
      console.error('Error checking API limits:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle API errors and show popup if limit exceeded
  const handleApiError = (error: any, apiType?: string): boolean => {
    if (error?.response?.status === 429 || error?.status === 429) {
      const errorData = error?.response?.data || error?.data || {};
      
      if (errorData.error === 'API_LIMIT_EXCEEDED') {
        setPopupDetails({
          apiType: errorData.apiType || apiType,
          currentUsage: errorData.currentUsage,
          limit: errorData.limit,
        });
        setShowPopup(true);
        setAnyLimitExceeded(true);
        
        // Refresh API usage data
        checkApiLimits();
        
        return true; // Indicates the error was handled
      }
    }
    return false; // Error was not a limit exceeded error
  };

  // Check if a specific API type has reached its limit
  const isLimitReached = (apiType: keyof ApiUsageData): boolean => {
    if (!apiUsage) return false;
    return apiUsage[apiType]?.exceeded || false;
  };

  // Check API limits when user changes or component mounts
  useEffect(() => {
    if (user) {
      checkApiLimits();
    }
  }, [user]);

  // Check API limits periodically (every 5 minutes)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      checkApiLimits();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user]);

  // Set up global API error handler
  useEffect(() => {
    setApiErrorHandler(handleApiError);
    
    // Cleanup on unmount
    return () => {
      setApiErrorHandler(null);
    };
  }, []);

  const closePopup = () => {
    setShowPopup(false);
    setPopupDetails({});
  };

  const value: ApiLimitContextType = {
    apiUsage,
    loading,
    anyLimitExceeded,
    checkApiLimits,
    handleApiError,
    isLimitReached,
  };

  return (
    <ApiLimitContext.Provider value={value}>
      {children}
      <ApiLimitPopup
        isOpen={showPopup}
        onClose={closePopup}
        apiType={popupDetails.apiType}
        currentUsage={popupDetails.currentUsage}
        limit={popupDetails.limit}
      />
    </ApiLimitContext.Provider>
  );
};

// Hook to check API limits before making API calls
export const useApiCheck = () => {
  const { anyLimitExceeded, isLimitReached, handleApiError } = useApiLimits();

  const checkBeforeCall = (apiType: keyof ApiUsageData): boolean => {
    if (anyLimitExceeded || isLimitReached(apiType)) {
      // Show popup for this specific API type
      handleApiError({
        response: {
          status: 429,
          data: {
            error: 'API_LIMIT_EXCEEDED',
            apiType,
            message: 'API limit reached. Please come back tomorrow for new usage limits.',
          }
        }
      }, apiType);
      return false;
    }
    return true;
  };

  return {
    checkBeforeCall,
    handleApiError,
    anyLimitExceeded,
  };
}; 